import { apiClient } from './client';
import { unwrapList } from './unwrap';
import type { PagedResult } from './types';

/** Matches Aperture.Application.Judging.Dtos.JudgeDto exactly. */
export interface JudgeAssignment {
  id: string;
  competitionId: string;
  userId: string;
  assignedAt: string;
}

/** Matches Aperture.Application.Judging.Dtos.JudgeCategoryProgressDto. */
export interface JudgeCategoryProgress {
  categoryId: string;
  scored: number;
  total: number;
}

/**
 * Matches Aperture.Application.Judging.Dtos.JudgeProgressDto: how many of the
 * competition's accepted entries one judge has scored, overall and per category.
 */
export interface JudgeProgress {
  judgeId: string;
  userId: string;
  scored: number;
  total: number;
  lastScoredAt?: string | null;
  categories: JudgeCategoryProgress[];
}

/**
 * Matches Aperture.Application.Judging.Dtos.AssignJudgeRequest exactly:
 * AssignJudgeRequest(Guid? UserId, string? Email, string? FirstName, string? LastName).
 * Either an existing account (userId) or a brand-new one to create on the fly
 * (email/firstName/lastName) - exactly one of the two shapes.
 */
export interface AssignJudgeRequest {
  userId?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

/**
 * Per-competition judge assignment - see JudgingController's
 * GetJudges/AssignJudge/RemoveJudge (GET/POST/DELETE
 * /api/v1/judging/competitions/{competitionId}/judges[/{judgeId}]).
 */
export const judgesApi = {
  listForCompetition: (competitionId: string) =>
    apiClient
      .get(`/judging/competitions/${competitionId}/judges`)
      .then((r) => unwrapList<JudgeAssignment>(r.data)) as Promise<PagedResult<JudgeAssignment>>,

  /** GET /judging/competitions/{competitionId}/judge-progress - organizer view of each judge's scoring progress. */
  progress: (competitionId: string) =>
    apiClient
      .get(`/judging/competitions/${competitionId}/judge-progress`)
      .then((r) => unwrapList<JudgeProgress>(r.data)) as Promise<PagedResult<JudgeProgress>>,

  assign: (competitionId: string, payload: AssignJudgeRequest) =>
    apiClient
      .post<JudgeAssignment>(`/judging/competitions/${competitionId}/judges`, payload)
      .then((r) => r.data),

  remove: (competitionId: string, judgeId: string) =>
    apiClient.delete(`/judging/competitions/${competitionId}/judges/${judgeId}`).then((r) => r.data),
};
